<template>
  <div class="filters-panel flex gap-3">
    <div v-if="error" class="notification is-danger is-light mb-4">
      <button class="delete" @click="$emit('clear-error')"></button>
      <strong>Error:</strong> {{ error }}
    </div>

    <div class="filter-block flex">
      <span class="filter-block-title">money</span>
      <div class="flex row gap-3 flex-center">
        <div class="input-box">
          <label class="has-text-grey-light">Deposit, $</label>
          <input
            type="number"
            class="input input-num"
            :value="value.deposit"
            @input="updateField('deposit', +$event.target.value)"
          />
        </div>
        <div class="input-box">
          <label class="has-text-grey-light">Stake, $</label>
          <input
            type="number"
            class="input input-num"
            :value="value.stake"
            @input="updateField('stake', +$event.target.value)"
          />
        </div>
      </div>
    </div>

    <div class="filter-block flex">
      <span class="filter-block-title">odds</span>
      <div class="flex row gap-3 flex-center">
        <div class="input-box">
          <label class="has-text-grey-light">Odds min</label>
          <input
            type="number"
            class="input input-num"
            :value="value.minOdds"
            step="0.01"
            @input="updateField('minOdds', +$event.target.value)"
          />
        </div>
        <div class="input-box">
          <label class="has-text-grey-light">Odds max</label>
          <input
            type="number"
            class="input input-num"
            :value="value.maxOdds"
            step="0.01"
            @input="updateField('maxOdds', +$event.target.value)"
          />
        </div>
      </div>
    </div>

    <div class="filter-block flex">
      <span class="filter-block-title">bots</span>
      <div class="flex row gap-3 flex-center">
        <Checkbox
          v-for="signal in signals"
          :key="signal"
          :name="signal"
          :title="signal.charAt(0).toUpperCase() + signal.slice(1)"
          class="mini"
          classMarkName="big white"
          :value="value.signal && value.signal.includes(signal)"
          @input="toggleSignal(signal, $event)"
        />
      </div>
    </div>

    <div class="filter-block flex">
      <span class="filter-block-title">time</span>
      <div class="flex row gap-3 flex-center">
        <button
          v-for="f in timeFilters"
          :key="f.label"
          class="button is-small"
          :class="time === f.label && 'is-success'"
          @click="setTimeFilter(f)"
        >
          {{ f.label }}
        </button>
        <Checkbox
          name="StoppageTime"
          title="Stoppage Time"
          class="mini"
          classMarkName="big white"
          :value="value.isStoppageTime"
          @input="updateField('isStoppageTime', $event)"
        />
        <div class="flex row gap-3">
          <div class="input-box">
            <label class="has-text-grey-light">From</label>
            <input
              type="number"
              class="input input-num"
              max="90"
              min="0"
              :value="value.minTime"
              @change="updateField('minTime', +$event.target.value)"
            />
          </div>
          <div class="input-box">
            <label class="has-text-grey-light">To</label>
            <input
              type="number"
              class="input input-num"
              max="90"
              min="0"
              :value="value.maxTime"
              @change="updateField('maxTime', +$event.target.value)"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="filter-block flex">
      <span class="filter-block-title">time</span>
      <div class="flex row gap-3 flex-center">
        <button
          v-for="f in quickFilters"
          :key="f.key"
          class="button is-small"
          :class="date === f.key && 'is-success'"
          @click="setQuickFilter(f.key)"
        >
          {{ f.label }}
        </button>
        <div class="flex row gap-3" style="width: 270px">
          <div class="input-box">
            <label class="has-text-grey-light">From</label>
            <input
              type="date"
              class="input input-date"
              :value="value.from"
              @change="setDateFrom($event.target.value)"
            />
          </div>
          <div class="input-box">
            <label class="has-text-grey-light">To</label>
            <input
              type="date"
              class="input input-date"
              :value="value.to"
              @change="setDateTo($event.target.value)"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="filter-block flex">
      <span class="filter-block-title">bots</span>
      <div class="flex row gap-3 flex-center">
        <Checkbox
          v-for="type in types"
          :key="type.label"
          :name="type.label"
          :title="type.label"
          :disabled='value.type === type.value'
          class="mini"
          classMarkName="big white"
          :value="value.type === type.value"
          @input="toggleType(type.value)"
        />
      </div>
    </div>
    <div class="filter-block flex">
      <span class="filter-block-title">bots</span>
      <div class="flex row gap-3 flex-center">
        <Checkbox
          v-for="part in parts"
          :key="part.label"
          :name="part.label"
          :title="part.label"
          :disabled='value.part === part.value'
          class="mini"
          classMarkName="big white"
          :value="value.part === part.value"
          @input="togglePart(part.value)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Checkbox from "../Checkbox.vue";

export default {
  name: "FiltersPanel",
  components: { Checkbox },
  props: {
    error: String,
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      signals: ["live", "raise", "crash", "ticker", "prematch"],
      types: [
        { label: "All", value: null },
        { label: "Over", value: "over" },
        { label: "HDP", value: "hdp" },
      ],
      parts: [
        { label: "All", value: null },
        { label: "FT", value: "FT" },
        { label: "HT", value: "HT" },
      ],
      date: "month_start",
      time: null,
      quickFilters: [
        { key: "today", label: "Today" },
        { key: "week", label: "Week" },
        { key: "month", label: "Month" },
        { key: "month_start", label: "Month start" },
      ],
      timeFilters: [
        { label: "0-15", min: 0, max: 15, isStoppageTime: false },
        { label: "16-30", min: 16, max: 30, isStoppageTime: false },
        { label: "31-45", min: 31, max: 45, isStoppageTime: false },
        { label: "45+", min: 45, max: 15, isStoppageTime: true },
        { label: "46-60", min: 46, max: 60, isStoppageTime: false },
        { label: "61-75", min: 61, max: 75, isStoppageTime: false },
        { label: "76-90", min: 76, max: 90, isStoppageTime: false },
        { label: "90+", min: 90, max: 15, isStoppageTime: true },
      ],
    };
  },
  methods: {
    updateField(key, value) {
      this.$emit("input", {
        ...this.value,
        [key]: value,
      });
    },
    setDateTo(value) {
      this.date = null;
      this.updateField("to", value);
    },
    setDateFrom(value) {
      this.date = null;
      this.updateField("from", value);
    },
    setQuickFilter(filter) {
      const now = new Date();
      let from = this.value.from;
      let to = this.value.to;

      if (filter === "today") {
        from = to = now.toISOString().slice(0, 10);
      } else if (filter === "week") {
        const weekAgo = new Date(now.getTime() - 7 * 86400000);
        from = weekAgo.toISOString().slice(0, 10);
        to = now.toISOString().slice(0, 10);
      } else if (filter === "month") {
        const monthAgo = new Date(now.getTime() - 30 * 86400000);
        from = monthAgo.toISOString().slice(0, 10);
        to = now.toISOString().slice(0, 10);
      } else if (filter === "month_start") {
        const start = new Date(
          Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
        );
        from = start.toISOString().slice(0, 10);
        to = now.toISOString().slice(0, 10);
      }
      this.date = filter;
      this.$emit("input", { ...this.value, from, to });
    },
    setTimeFilter({ label, min, max, isStoppageTime }) {
      this.time = label;
      this.$emit("input", {
        ...this.value,
        minTime: min,
        maxTime: max,
        isStoppageTime,
      });
    },
    toggleSignal(signal, checked) {
      if (signal === "all") return this.updateField("signal", this.signals);

      const list = this.value.signal ? [...this.value.signal] : [];

      if (checked && !list.includes(signal)) {
        list.push(signal);
      }

      if (!checked && list.includes(signal)) {
        list.splice(list.indexOf(signal), 1);
      }

      if (list.length) {
        this.updateField("signal", list);
      } else {
        this.updateField("signal", this.signals);
      }
    },
    toggleType(type) {
      this.updateField("type", this.value.type === type ? null : type);
    },
    togglePart(part) {
      this.updateField("part", this.value.part === part ? null : part);
    },
  },
  mounted() {
    const now = new Date();
    const start = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
    );
    this.$emit("input", {
      ...this.value,
      from: start.toISOString().slice(0, 10),
      to: now.toISOString().slice(0, 10),
      signal: this.signals,
    });
  },
};
</script>

<style scoped lang="scss">
.filters-panel {
  margin-bottom: 10px;
}
.flex {
  display: flex;
  &.row {
    flex-wrap: wrap;
  }
  &.gap-3 {
    gap: 6px;
  }
  .flex-center {
    justify-content: space-evenly;
    align-content: center;
    align-items: center;
  }
}

.filter-block {
  position: relative;
  padding: 7px 8px 3px;
  border-radius: 6px;
  border: solid 1px $brandColor;
  background-color: $cardFontColor;

  .filter-block-title {
    position: absolute;
    top: -12px;
    left: 10px;
    padding: 0 8px;
    background-color: inherit;
    border-radius: 6px;
    border-top: solid 1px $brandColor;
    line-height: normal;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: -1px;
      border-radius: 6px 6px 0 0;
      width: calc(100% + 2px);
      height: 60%;
      border: 1px solid $brandColor;
      pointer-events: none;
      border-bottom-style: none;
    }
  }
  .input-box {
    font-size: 0.75rem;
    font-weight: bold;

    label {
      display: block;
    }
  }
  .label {
    margin: 0;
    padding: 2px;
    height: 28px;
    font-size: 15px;
  }
  .input {
    font-size: 14px;
    padding: 0 2px;
  }
}
.dark-box {
  background-color: #1e1e1e;
  border: 1px solid #2a2a2a;
}

.input-num {
  width: 80px;
}

.input-date {
  width: 130px;
}

.button.is-dark {
  background-color: #2a2a2a;
  color: #fff;

  &:hover {
    background-color: #363636;
  }
}
</style>
